/**
 * Botón de Staff (design-system §7 · Botón y §8.1).
 *
 * - `primary` (por defecto): Coral 500 sólido, 56 px, ancho completo, etiqueta Nunito 18 px/700.
 *   Es el único elemento coral sólido de la pantalla y solo cumple AA con etiqueta grande y bold
 *   (§3.3); hover Coral 600 (`--fm-primary-hover`), pressed Coral 700 (`--fm-primary-compact`).
 * - `secondary`: contorno marino. `ghost`: terciario (cancelar). `destructive`: error 600.
 * - Tamaños: `lg` 56 px (el primario) y `md` 48 px; ambos superan los 44 px táctiles de §10.
 * - Foco: anillo 2 px `--fm-focus` con offset 2 px. `loading` reemplaza la etiqueta por un texto
 *   de progreso (nunca un spinner solo) y bloquea el botón. Solo tokens `--fm-*`.
 *
 * Para un `<Link>` de TanStack Router con aspecto de botón usa `buttonClasses(...)` como className.
 */
import { t } from '../../i18n';

import { cn } from './cn';

import type { ButtonHTMLAttributes, ReactNode, Ref } from 'react';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'destructive';
export type ButtonSize = 'lg' | 'md';

const BASE_CLASSES =
  'inline-flex items-center justify-center gap-fm-2 rounded-fm-md select-none ' +
  'transition-colors duration-fm-fast ease-fm-standard ' +
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-fm-focus ' +
  'disabled:opacity-45 disabled:shadow-none disabled:pointer-events-none ' +
  'motion-reduce:transition-none';

const VARIANT_CLASSES: Record<ButtonVariant, string> = {
  primary:
    'bg-fm-primary text-fm-on-primary font-display font-bold ' +
    'hover:bg-fm-primary-hover active:bg-fm-primary-compact',
  secondary:
    'bg-transparent text-fm-text border border-fm-text font-ui font-semibold ' +
    'hover:bg-fm-bg-sunken active:bg-fm-bg-sunken',
  ghost: 'bg-transparent text-fm-text font-ui font-medium hover:bg-fm-bg-sunken active:bg-fm-bg-sunken',
  destructive:
    'bg-fm-danger text-fm-text-inverse font-ui font-semibold hover:brightness-95 active:brightness-90',
};

const SIZE_CLASSES: Record<ButtonSize, string> = {
  lg: 'h-fm-control-lg px-fm-6 text-fm-lg',
  md: 'h-fm-control-md px-fm-4 text-fm-base',
};

export interface ButtonClassOptions {
  variant?: ButtonVariant;
  /** Por defecto `lg` para `primary` y `md` para el resto. */
  size?: ButtonSize;
  /** Ancho completo (por defecto en Staff, §8.1). */
  fullWidth?: boolean;
  className?: string;
}

/** Clases del botón para reutilizarlas en un `<Link>` u otro elemento. */
export function buttonClasses({
  variant = 'primary',
  size,
  fullWidth = true,
  className,
}: ButtonClassOptions = {}): string {
  const resolvedSize: ButtonSize = size ?? (variant === 'primary' ? 'lg' : 'md');
  return cn(
    BASE_CLASSES,
    VARIANT_CLASSES[variant],
    SIZE_CLASSES[resolvedSize],
    fullWidth && 'w-full',
    className,
  );
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement>, ButtonClassOptions {
  /** Muestra el texto de progreso y deshabilita el botón. */
  loading?: boolean;
  /** Texto de progreso; por defecto `common.states.processing`. */
  loadingText?: string;
  /** Icono Lucide de 20 px a la izquierda (se oculta mientras carga). */
  icon?: ReactNode;
  ref?: Ref<HTMLButtonElement>;
}

export function Button({
  variant = 'primary',
  size,
  fullWidth = true,
  loading = false,
  loadingText,
  icon,
  className,
  children,
  disabled,
  type,
  ...props
}: ButtonProps) {
  return (
    <button
      type={type ?? 'button'}
      className={buttonClasses({ variant, size, fullWidth, className })}
      disabled={disabled ?? loading}
      aria-busy={loading || undefined}
      {...props}
    >
      {loading ? (
        <span>{loadingText ?? t('common.states.processing')}</span>
      ) : (
        <>
          {icon ? <span aria-hidden="true">{icon}</span> : null}
          {children}
        </>
      )}
    </button>
  );
}
